import { fetchAuthSession } from 'aws-amplify/auth';
import type {
    SupplierControlService,
    ControlSupplier,
    ControlRequest,
    ControlTeam,
    ControlUser,
    OnboardedSupplier,
    TeamAssignment,
} from '../types';
import { getAwsConfig } from '../../config/appConfig';
import { ensureAmplifyConfigured } from './amplifyConfig';

/**
 * Supplier Control (internal access management) service.
 *
 * Users, Approval Queue, and the Onboarded Suppliers directory are backed by
 * the control API (Cognito-derived). The per-site access model (organizations,
 * sites, per-login access) needs a data store that isn't built yet, so
 * listSuppliers returns empty and setSiteAccess is a no-op until that follow-up
 * lands.
 */

async function controlUrl(path: string): Promise<string> {
    const base = getAwsConfig().asnApiEndpoint;
    if (!base) {
        throw new Error('ASN API endpoint is not configured.');
    }
    return `${base.replace(/\/$/, '')}/v1/control${path}`;
}

async function authHeader(): Promise<Record<string, string>> {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) {
        throw new Error('Not authenticated.');
    }
    return { Authorization: token };
}

export const awsSupplierControlService: SupplierControlService = {
    async listSuppliers(): Promise<ControlSupplier[]> {
        // Per-site access model (sites + per-login access) is a separate follow-up.
        return [];
    },

    async listOnboardedSuppliers(): Promise<OnboardedSupplier[]> {
        ensureAmplifyConfigured();
        const res = await fetch(await controlUrl('/suppliers'), { headers: await authHeader() });
        if (!res.ok) {
            throw new Error(`Failed to load suppliers: ${res.status} ${res.statusText}`);
        }
        const body = (await res.json()) as { suppliers: OnboardedSupplier[] };
        return body.suppliers;
    },

    async listRequests(): Promise<ControlRequest[]> {
        ensureAmplifyConfigured();
        const res = await fetch(await controlUrl('/requests'), { headers: await authHeader() });
        if (!res.ok) {
            throw new Error(`Failed to load requests: ${res.status} ${res.statusText}`);
        }
        const body = (await res.json()) as { requests: ControlRequest[] };
        return body.requests;
    },

    async listTeams(domain: string): Promise<ControlTeam[]> {
        ensureAmplifyConfigured();
        const res = await fetch(
            await controlUrl(`/teams?domain=${encodeURIComponent(domain)}`),
            { headers: await authHeader() },
        );
        if (!res.ok) {
            throw new Error(`Failed to load teams: ${res.status} ${res.statusText}`);
        }
        const body = (await res.json()) as { teams: ControlTeam[] };
        return body.teams;
    },

    async listTeamMembers(
        teamId: string,
        cursor?: string,
    ): Promise<{ members: string[]; nextCursor: string | null }> {
        ensureAmplifyConfigured();
        const suffix = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
        const res = await fetch(
            await controlUrl(`/teams/${encodeURIComponent(teamId)}/members${suffix}`),
            { headers: await authHeader() },
        );
        if (!res.ok) {
            throw new Error(`Failed to load team members: ${res.status} ${res.statusText}`);
        }
        const body = (await res.json()) as { members: string[]; nextCursor: string | null };
        return { members: body.members, nextCursor: body.nextCursor };
    },

    async createTeam(domain: string, teamName: string): Promise<ControlTeam> {
        ensureAmplifyConfigured();
        const res = await fetch(await controlUrl('/teams'), {
            method: 'POST',
            headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
            body: JSON.stringify({ domain, teamName }),
        });
        if (!res.ok) {
            throw new Error(`Failed to create team: ${res.status} ${res.statusText}`);
        }
        const body = (await res.json()) as { team: ControlTeam };
        return body.team;
    },

    async approveRequest(
        requestId: string,
        assignment?: TeamAssignment,
    ): Promise<ControlRequest[]> {
        ensureAmplifyConfigured();
        // Team assignment: an existing team (teamId) to share its credential +
        // reporting partition, or a new team (teamName) created under the
        // login's domain. Omitted -> the backend makes the login its own team.
        const payload: TeamAssignment = {};
        if (assignment?.teamId?.trim()) payload.teamId = assignment.teamId.trim();
        else if (assignment?.teamName?.trim()) payload.teamName = assignment.teamName.trim();
        const res = await fetch(
            await controlUrl(`/requests/${encodeURIComponent(requestId)}/approve`),
            {
                method: 'POST',
                headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            },
        );
        if (!res.ok) {
            throw new Error(`Failed to approve request: ${res.status} ${res.statusText}`);
        }
        const body = (await res.json()) as { requests: ControlRequest[] };
        return body.requests;
    },

    async reassignTeam(username: string, assignment: TeamAssignment): Promise<void> {
        ensureAmplifyConfigured();
        const payload: TeamAssignment = {};
        if (assignment.teamId?.trim()) payload.teamId = assignment.teamId.trim();
        else if (assignment.teamName?.trim()) payload.teamName = assignment.teamName.trim();
        const res = await fetch(
            await controlUrl(`/users/${encodeURIComponent(username)}/team`),
            {
                method: 'POST',
                headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            },
        );
        if (!res.ok) {
            throw new Error(`Failed to reassign team: ${res.status} ${res.statusText}`);
        }
    },

    async listUsers(): Promise<ControlUser[]> {
        ensureAmplifyConfigured();
        const res = await fetch(await controlUrl('/users'), { headers: await authHeader() });
        if (!res.ok) {
            throw new Error(`Failed to load users: ${res.status} ${res.statusText}`);
        }
        const body = (await res.json()) as { users: ControlUser[] };
        return body.users;
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async setSiteAccess(email: string, siteId: string, granted: boolean): Promise<void> {
        // No-op until the suppliers/sites access-management model exists.
    },
};

export default awsSupplierControlService;
